import { isNativePlatform } from '@/lib/platform';
import { Browser } from '@capacitor/browser';

export type GygOpenResult =
  | { status: 'opened'; url: string }
  | { status: 'blocked'; url: string };

export interface OpenGygInNewTabOptions {
  url: string;
  onSuccess?: (url: string) => void;
  onBlocked?: (url: string) => void;
}

/**
 * Opens a GetYourGuide affiliate URL in a new browser tab (web) or in-app browser (native).
 * Must be called synchronously from a user-gesture handler.
 */
export function openGygInNewTab(options: OpenGygInNewTabOptions): GygOpenResult {
  const { url, onSuccess, onBlocked } = options;

  if (typeof window === 'undefined') {
    onBlocked?.(url);
    return { status: 'blocked', url };
  }

  if (isNativePlatform()) {
    void Browser.open({ url }).then(() => {
      onSuccess?.(url);
    });
    return { status: 'opened', url };
  }

  // Omit noopener from window.open features — with noopener/noreferrer many browsers
  // return null even when the tab opened successfully, which false-triggers block UI.
  const popup = window.open(url, '_blank');
  if (!popup) {
    onBlocked?.(url);
    return { status: 'blocked', url };
  }

  try {
    popup.opener = null;
  } catch {
    // Tab may already be cross-origin; opener isolation is best-effort.
  }

  onSuccess?.(url);
  return { status: 'opened', url };
}
